// Side column rendered next to the piece image inside the lightbox.
// Borderless/frameless on purpose — the lightbox backdrop is near-black, so the
// panel reads as flowing text on the same surface, not a separate card.

import { For, Show } from "solid-js";
import type { Piece, PieceSocials } from "../lib/cosmology";
import { useLocale } from "../i18n/LocaleProvider";
import { isLiked, toggleLike } from "../lib/likes";

interface Props {
  piece: Piece;
  prevId?: string | null;
  nextId?: string | null;
  onNav?: (id: string) => void;
}

interface SocialEntry {
  kind: "x" | "twitter" | "instagram" | "telegram" | "web";
  handle: string;
  href: string;
  label: string;
}

function socialEntries(s: PieceSocials | undefined): SocialEntry[] {
  if (!s) return [];
  const out: SocialEntry[] = [];
  if (s.x) out.push({ kind: "x", handle: s.x, href: `https://x.com/${s.x}`, label: "X" });
  if (s.twitter) out.push({ kind: "twitter", handle: s.twitter, href: `https://twitter.com/${s.twitter}`, label: "Twitter" });
  if (s.instagram) out.push({ kind: "instagram", handle: s.instagram, href: `https://instagram.com/${s.instagram}`, label: "Instagram" });
  if (s.telegram) out.push({ kind: "telegram", handle: s.telegram, href: `https://t.me/${s.telegram}`, label: "Telegram" });
  if (s.web) out.push({ kind: "web", handle: s.web.replace(/^https?:\/\//, ""), href: s.web, label: "Web" });
  return out;
}

// Inline SVGs — single-color, currentColor stroke/fill. Sized via Tailwind.
function SocialIcon(props: { kind: SocialEntry["kind"] }) {
  const cls = "w-6 h-6 shrink-0";
  switch (props.kind) {
    case "instagram":
      return (
        <svg class={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" stroke="none" />
        </svg>
      );
    case "x":
    case "twitter":
      return (
        <svg class={cls} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
        </svg>
      );
    case "telegram":
      return (
        <svg class={cls} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M21.5 3.5 2.6 10.9c-1 .4-1 1 0 1.3l4.8 1.5 1.9 5.8c.2.6.5.8 1 .4l2.7-2.4 4.9 3.6c.9.5 1.5.2 1.7-.8L22.5 4.7c.3-1.2-.4-1.7-1-1.2Zm-3.1 4.1-8.2 7.3-.3 3.3-1.7-5 10.2-5.6Z" />
        </svg>
      );
    case "web":
      return (
        <svg class={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.8 3 2.8 15 0 18M12 3c-2.8 3-2.8 15 0 18" />
        </svg>
      );
  }
}

function formatDate(unixSecs: number, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(unixSecs * 1000));
  } catch {
    return new Date(unixSecs * 1000).toLocaleDateString();
  }
}

export default function PieceAuthorPanel(props: Props) {
  const { t, locale } = useLocale();
  const p = () => props.piece;
  const nick = () => p().authorNickname;
  const brand = () => p().authorBrand;
  const slogan = () => p().authorSlogan;
  const socials = () => socialEntries(p().authorSocials);
  const isDemo = () => p().kind === "demo";
  const likes = () => p().likes ?? 0;
  const heartActive = () => isLiked(p().id) || p().myVote === 1;
  const createdAt = () => p().createdAt;

  return (
    <aside
      class="w-full md:w-72 max-w-[90vw] flex flex-col gap-6 text-sm text-stone-200 pt-8 md:pt-10"
      data-testid="piece-author-panel"
    >
      {/* Header: nickname (large) + brand + slogan. No avatar, no badge. */}
      <div class="flex flex-col gap-1.5">
        <Show
          when={nick()}
          fallback={<div class="font-display text-2xl text-stone-300 italic tracking-tight">Anonymous</div>}
        >
          <div class="font-display text-2xl text-amber-100 truncate tracking-tight">@{nick()}</div>
        </Show>
        <Show when={brand()}>
          <div class="text-xs text-stone-300 truncate uppercase tracking-widest">{brand()}</div>
        </Show>
        <Show when={slogan()}>
          <p class="text-sm italic text-stone-100 leading-relaxed pt-2 font-display">{slogan()}</p>
        </Show>
      </div>

      <Show when={createdAt()}>
        <div class="text-xs text-stone-400">
          {t("lightboxCreated", { date: formatDate(createdAt() as number, locale()) })}
        </div>
      </Show>

      {/* Socials — icon + handle. Larger touch target + recognisable glyphs. */}
      <Show when={socials().length > 0}>
        <div class="flex flex-col gap-3">
          <span class="text-stone-400 uppercase tracking-[0.2em] text-[10px]">Socials</span>
          <For each={socials()}>
            {(s) => (
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-3 text-stone-200 hover:text-amber-100 transition group"
                data-social={s.kind}
                aria-label={`${s.label}: ${s.handle}`}
              >
                <span class="text-stone-300 group-hover:text-amber-100 transition">
                  <SocialIcon kind={s.kind} />
                </span>
                <span class="truncate text-sm">{s.handle}</span>
              </a>
            )}
          </For>
        </div>
      </Show>

      <Show when={isDemo()}>
        <div class="text-[10px] uppercase tracking-[0.25em] text-stone-500 pt-2">
          Demo space · curated preview
        </div>
      </Show>

      {/* Heart-only vote + prev/next chevrons. v9 retired rank. */}
      <div class="flex flex-col gap-4 pt-4 border-t border-white/10" data-testid="piece-rank-rail">
        <button
          type="button"
          class={`inline-flex items-center gap-3 self-start transition ${
            heartActive() ? "text-rose-400" : "text-stone-300 hover:text-rose-300"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            toggleLike(p().id);
          }}
          aria-pressed={heartActive()}
          data-testid="lightbox-like"
        >
          <span aria-hidden="true" class="text-3xl leading-none">
            {heartActive() ? "♥" : "♡"}
          </span>
          <span class="tabular-nums text-lg font-medium">{likes()}</span>
          <span class="text-stone-500 text-xs uppercase tracking-wider">{t("lightboxLikes")}</span>
        </button>

        <Show when={props.prevId || props.nextId}>
          <div class="flex items-center justify-between text-xs text-stone-400 pt-1">
            <Show when={props.prevId} fallback={<span />}>
              <button
                type="button"
                class="hover:text-amber-100 transition"
                onClick={(e) => {
                  e.stopPropagation();
                  if (props.prevId && props.onNav) props.onNav(props.prevId);
                }}
                data-testid="lightbox-prev"
              >
                ← {t("lightboxPrev")}
              </button>
            </Show>
            <Show when={props.nextId} fallback={<span />}>
              <button
                type="button"
                class="hover:text-amber-100 transition"
                onClick={(e) => {
                  e.stopPropagation();
                  if (props.nextId && props.onNav) props.onNav(props.nextId);
                }}
                data-testid="lightbox-next"
              >
                {t("lightboxNext")} →
              </button>
            </Show>
          </div>
        </Show>
      </div>
    </aside>
  );
}
